import 'moment/locale/en-gb';
import 'moment/locale/pl';
import { Column } from "primereact/column";
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ReactComponent as ArrowDownIcon } from '../../assets/images/arrow_down.svg';
import { ReactComponent as ArrowUpIcon } from '../../assets/images/arrow_up.svg';
import { ReactComponent as ProductIcon } from '../../assets/images/product-icon.svg';
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from "../../component/datatable2";
import PeriodSelector from '../../component/periodselector/PeriodSelector';
import { MessageContext } from '../../context/MessageContext';
import { useDashboardData } from '../../context/DashboardContext';
import { TransactionService } from '../../service/TransactionService';
import { FormattedUtils } from '../../utils/FormattedUtils';
import './SalesByProductWidget.scss';
import './Widget.scss';

const SalesByProductWidget = () => {
    return (
        <DataTableProvider>
            <SalesByProductWidgetImpl />
        </DataTableProvider>
    );
};

const SalesByProductToolbar = ({
    rowsPerPageOptions,
    defaultSearchCriteria,
    period,
    setPeriod,
    isCollapsed,
    toggleCollapse,
}) => {
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);

    return (
        <div className="flex align-items-center gap-2">
            {!isCollapsed && (
                <>
                    <Dropdown
                        className="small"
                        id="rowsPerPage"
                        options={rowsPerPageOptions}
                        value={searchCriteria?.rows ?? defaultSearchCriteria.rows}
                        onChange={e =>
                            setSearchCriteria(prev => ({
                                ...prev,
                                first: 0,
                                rows: e.value,
                                timestamp: Date.now(),
                            }))
                        }
                    />

                    <PeriodSelector
                        id="period"
                        value={period}
                        onChange={period => {
                            if (period?.from && period?.to && period?.periodType) {
                                setPeriod(period);

                                setSearchCriteria(prev => ({
                                    ...prev,
                                    first: 0,
                                    timestamp: Date.now(),
                                }));
                            }
                        }}
                        defaultFilterType="currentWeek"
                    />
                </>
            )}

            {isCollapsed ? (
                <ArrowUpIcon className="collapse-button" onClick={toggleCollapse} />
            ) : (
                <ArrowDownIcon className="collapse-button" onClick={toggleCollapse} />
            )}
        </div>
    );
};

const SalesByProductWidgetImpl = () => {

    const { t, i18n } = useTranslation();
    const isMounted = useRef(false);
    const rowsPerPageOptions = [5, 10, 20, 50];
    const { showErrorMessage } = useContext(MessageContext);
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);
    const { productOptions, categoryOptions } = useDashboardData();
    const [maxTransactionCount, setMaxTransactionCount] = useState(0);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [period, setPeriod] = useState();
    const defaultSearchCriteria = useMemo(() => ({
        first: 0,
        rows: 5,
        sortField: 'revenue',
        sortOrder: -1,
        baseQuery: '',
        filters: {}
    }), []);

    useEffect(() => {
        isMounted.current = true;
        setSearchCriteria(defaultSearchCriteria);
        return () => {
            isMounted.current = false;
        }
    }, []);

    

    useEffect(() => {
        if (isMounted.current && period && period.from && period.to && period.periodType) {
            setSearchCriteria(prev => ({ ...prev }));
            fetchMaxTransactionCount();
        }
    }, [period]);


    const filterConfig = useMemo(() => [
        {
            fieldName: 'product',
            label: t('salesByProduct.product'),
            type: FILTER_TYPES.LIST,
            params: productOptions,
            apiParamName: 'products',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'category',
            label: t('salesByProduct.category'),
            type: FILTER_TYPES.LIST,
            params: categoryOptions.map(c => ({ id: c.uuid, label: i18n.language === 'pl' ? c.name.namePl : c.name.name })),
            apiParamName: 'categories',
            apiValueConverter: value => value.map(r => r.id).join(',')
        },
        {
            fieldName: 'salesQuantity',
            label: t('salesByProduct.salesQuantity'),
            type: FILTER_TYPES.NUMBER_RANGE,
            apiParamName: 'sales_quantity',
            params: { maxValue: maxTransactionCount }
        },
        {
            fieldName: 'revenue',
            label: t('salesByProduct.revenue'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'revenue',
            params: {
                maxValue: 999999,
                minValue: 0
            }
        },
        {
            fieldName: 'growth',
            label: t('salesByProduct.growth'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'growth',
            params: {
                maxValue: 10000,
                minValue: -10000
            }
        },
        {
            fieldName: 'totalRevenuePercent',
            label: t('salesByProduct.totalRevenuePercent'),
            type: FILTER_TYPES.AMOUNT,
            apiParamName: 'total_revenue_percent',
            params: {
                maxValue: 1000,
                minValue: -1000
            }
        }

    ], [productOptions, categoryOptions, maxTransactionCount, i18n.language]);

    const normalizeCriteria = (criteria) => ({
        ...defaultSearchCriteria,
        ...criteria,
        first: criteria?.first ?? defaultSearchCriteria.first,
        rows: criteria?.rows ?? defaultSearchCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultSearchCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultSearchCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultSearchCriteria.baseQuery,
        filters: criteria?.filters ?? defaultSearchCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            if (!period) {
                return { data: [], totalRecords: 0 };
            }

            return await TransactionService.getTopSalesByProduct(period, normalizeCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('salesByProduct.error.fetch'));
            return { data: [], totalRecords: 0 };
        }
    };


    const fetchMaxTransactionCount = async () => {
        try {
            if (period) {
                const count = await TransactionService.getMaxTransactionCountForSalesByProduct(period);
                setMaxTransactionCount((!count || count < 25) ? 25 : count);
            }
        } catch (err) {
            showErrorMessage(t('salesByProduct.error.fetchMaxTransactionCount'));
        }
    }

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    }

    const tableToolbarLeft = (
        <div className='flex align-items-center gap-2'>
            <ProductIcon />
            <span className='widget-name'>{t('salesByProduct.widgetName')}</span>
        </div>
    );

    const tableToolbarRight = (
        <SalesByProductToolbar
            rowsPerPageOptions={rowsPerPageOptions}
            defaultSearchCriteria={defaultSearchCriteria}
            period={period}
            setPeriod={setPeriod}
            isCollapsed={isCollapsed}
            toggleCollapse={toggleCollapse}
        />
    );

    const growthTemplate = (row) => {
        const growth = row.growth;
        if (!growth) {
            return (
                <div className='growth-container'>
                    <span className='growth-value'>-</span>
                </div>
            );
        }
        let formattedGrowth = '';
        let color;
        if (growth > 0) {
            color = 'green';
            formattedGrowth = '+';
        } else if (growth < 0) {
            color = 'red';
        } else {
            color = 'gray';
        }
        formattedGrowth += FormattedUtils.formatPrice(growth) + '%';
        return (
            <div className='growth-container'>
                <span className={classNames('growth-value', color)}>{formattedGrowth}</span>
                {growth < 0 && (
                    <div className={classNames('status-colored', color)} style={{ width: 'fit-content' }}>
                        <div className={classNames('status-image', color)} />
                        <div className={classNames('status-name', color)}>{t('salesByProduct.salesDrop')}</div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className={`top-sales-by-product-widget widget data-card ${isCollapsed ? 'collapsed' : ''}`}>
            <DataTable
                tableToolbarLeft={tableToolbarLeft}
                tableToolbarRight={tableToolbarRight}
                globalSearchPosition="none"
                fetchData={fetchData}
                filterConfig={filterConfig}
                defaultSearchCriteria={defaultSearchCriteria}
                showRowsPerPage={false}
                showPaginator={false}
                minWidth="900px"
            >
                <Column field="product" header={t('salesByProduct.product')} sortable sortField="product" id="product"
                    style={{ minWidth: '160px', maxWidth: '220px' }}
                />
                <Column field="category" header={t('salesByProduct.category')} sortable sortField="category" id="category"
                    style={{ minWidth: '140px', maxWidth: '180px' }}
                />
                <Column field="salesQuantity" header={t('salesByProduct.salesQuantity')} sortable sortField="salesQuantity" id="salesQuantity"
                    headerClassName="dt2-header-wrap"
                    style={{ minWidth: '120px', maxWidth: '140px' }}
                />
                <Column field="revenue" header={t('salesByProduct.revenue')} sortable sortField="revenue" id="revenue"
                    body={row => FormattedUtils.formatPrice(row.revenue)}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
                <Column field="growth" header={t('salesByProduct.growth')} sortable sortField="growth" id="growth"
                    body={growthTemplate}
                    style={{ minWidth: '130px', maxWidth: '170px' }}
                    headerClassName="dt2-header-wrap"
                />
                <Column field="totalRevenuePercent" header={t('salesByProduct.totalRevenuePercent')} sortable sortField="totalRevenuePercent" id="totalRevenuePercent"
                    headerClassName="dt2-header-wrap"
                    body={row => FormattedUtils.formatPrice(row.totalRevenuePercent)}
                    style={{ minWidth: '120px', maxWidth: '150px' }}
                />
            </DataTable>
        </div>
    )

}

export default SalesByProductWidget;

